<script setup>
import {
    computed,
    nextTick,
    onBeforeUnmount,
    ref,
    useTemplateRef,
    watch,
} from 'vue'
import { useBillingStore } from '@/stores/billing'
import ProfileAccountBillingService from '@/services/profile-account-billing.service'
import { successMessage } from '@/utils/notifications'

/**
 * Add or replace the account's card. Stripe.js draws the card form and keeps
 * the number to itself; we only ever see the payment method id it hands back,
 * which the API then saves as the account's card. An account that is behind on
 * payment has its open invoice retried with the new card straight away.
 */
const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['close'])

const billingStore = useBillingStore()

const cardMount = useTemplateRef('card-mount')
const error = ref('')
const ready = ref(false)
const processing = ref(false)
// Set when the bank has to confirm the retried payment: the card is saved, and
// this is where the owner does that.
const authenticationUrl = ref(null)

// Stripe.js and the card field it draws, once the setup is open; the secret
// that setup is for; and the payment method Stripe has accepted until the API
// has saved it. A setup can only succeed once, so if saving fails the retry
// saves the accepted one instead of asking Stripe again.
let stripe
let cardElement
let clientSecret
let acceptedPaymentMethod
let requestId = 0

const hasCard = computed(() => !!billingStore.summary?.payment_method?.present)
const title = computed(() => (hasCard.value ? 'Update card' : 'Add a card'))
const retriesInvoice = computed(() =>
    ['past_due', 'suspended'].includes(billingStore.statusAlert?.kind)
)

const CARD_STYLE = {
    base: {
        fontSize: '16px',
        color: '#1c1917',
        '::placeholder': { color: '#a8a29e' },
    },
}

function teardown() {
    requestId += 1
    try {
        cardElement?.destroy()
    } catch {
        // Already gone with the form.
    }
    cardElement = undefined
    stripe = undefined
    clientSecret = undefined
    acceptedPaymentMethod = undefined
    ready.value = false
    processing.value = false
    authenticationUrl.value = null
}

async function setup() {
    const current = ++requestId
    error.value = ''
    authenticationUrl.value = null
    acceptedPaymentMethod = undefined

    try {
        if (!window.Stripe) {
            throw new Error(
                "We couldn't load the card form. Reload the page and try again."
            )
        }

        const response =
            await ProfileAccountBillingService.createPaymentMethod()
        // Closed, or opened again, while the setup was being created.
        if (current !== requestId) return

        clientSecret = response.data.client_secret
        stripe = window.Stripe(response.data.stripe_public_key)

        // The card field mounts into the slide-in's body, which only renders
        // while it is open.
        await nextTick()
        if (current !== requestId || !cardMount.value) return

        const elements = stripe.elements()
        cardElement = elements.create('card', { style: CARD_STYLE })
        cardElement.mount(cardMount.value)
        ready.value = true
    } catch (e) {
        if (current !== requestId) return
        error.value =
            e?.response?.data?.message ??
            e?.message ??
            "We couldn't set up the card form. Try again."
    }
}

async function save() {
    if (!stripe || !cardElement || processing.value) return

    processing.value = true
    error.value = ''

    try {
        if (!acceptedPaymentMethod) {
            const result = await stripe.confirmCardSetup(clientSecret, {
                payment_method: { card: cardElement },
            })

            if (!result.setupIntent) {
                error.value =
                    result.error?.message ??
                    "We couldn't check that card. Try another one."
                return
            }

            acceptedPaymentMethod = result.setupIntent.payment_method
        }

        const response =
            await ProfileAccountBillingService.confirmPaymentMethod(
                acceptedPaymentMethod
            )
        acceptedPaymentMethod = undefined

        // The answer is the billing summary, plus how the retry of an unpaid
        // invoice on the new card went (only for an account behind on payment).
        const { card_update: cardUpdate, ...summary } = response.data
        await billingStore.applySummary(summary)

        finish(cardUpdate)
    } catch (e) {
        error.value =
            e?.response?.data?.message ??
            "We couldn't save that card. Try again."
    } finally {
        processing.value = false
    }
}

// Saving the card on an account that is behind on payment retries its unpaid
// invoice with the card straight away. What that came to decides what the owner
// sees next.
function finish(cardUpdate) {
    switch (cardUpdate?.retry_outcome) {
        case 'declined': {
            // The card is saved, but the bank said no to the payment. A setup
            // only works once, so the form starts again for another card.
            const message =
                'We saved your card, but your bank declined the payment of your unpaid invoice. Try another card.'
            teardown()
            void setup().then(() => {
                if (!error.value) error.value = message
            })
            return
        }
        case 'requires_action':
            if (cardUpdate.authentication_url) {
                authenticationUrl.value = cardUpdate.authentication_url
                return
            }
            successMessage('Your card is saved.')
            break
        case 'succeeded':
            successMessage(
                'Your card is saved and your unpaid invoice is paid.'
            )
            break
        case 'unknown':
            successMessage(
                "Your card is saved. We're still checking the payment of your unpaid invoice."
            )
            break
        default:
            successMessage('Your card is saved.')
    }

    emit('close')
}

watch(
    () => props.isOpen,
    (open) => {
        if (open) {
            void setup()
        } else {
            teardown()
        }
    },
    { immediate: true }
)

onBeforeUnmount(teardown)
</script>

<template>
    <zen-slide-in
        :is-open="isOpen"
        :title="title"
        :error="error"
        @close="emit('close')"
    >
        <template #body>
            <p
                v-if="authenticationUrl"
                class="text-sm text-taupe-700"
                data-testid="card-confirm-payment"
            >
                Your card is saved. Your bank needs you to confirm the payment
                of your unpaid invoice before it goes through.
            </p>

            <template v-else>
                <p class="mb-6 text-sm text-taupe-600">
                    <template v-if="retriesInvoice"
                        >We'll retry your unpaid invoice with this card as soon
                        as it's saved. </template
                    >We use it for your recurring payments.
                </p>

                <label
                    for="card-details"
                    class="block text-sm font-medium leading-6 text-taupe-900"
                    >Card details</label
                >
                <div
                    id="card-details"
                    ref="card-mount"
                    data-testid="card-mount"
                    class="mt-2 w-full rounded-lg border border-taupe-300 bg-white p-4"
                ></div>
                <p v-if="!ready && !error" class="mt-2 text-xs text-taupe-500">
                    Loading the card form…
                </p>

                <p
                    class="mt-6 rounded-lg bg-taupe-50 p-4 text-xs text-taupe-600"
                >
                    Stripe processes your card and keeps the number. It never
                    touches our servers.
                </p>
            </template>
        </template>

        <template #buttons>
            <template v-if="authenticationUrl">
                <zen-button
                    type="button"
                    variant="secondary"
                    @click="emit('close')"
                >
                    Not now
                </zen-button>
                <zen-button
                    :href="authenticationUrl"
                    variant="primary"
                    data-testid="card-confirm-payment-link"
                >
                    Confirm payment
                </zen-button>
            </template>
            <template v-else>
                <zen-button
                    type="button"
                    variant="secondary"
                    @click="emit('close')"
                >
                    Cancel
                </zen-button>
                <zen-button
                    type="button"
                    variant="primary"
                    :disabled="!ready"
                    :processing="processing"
                    data-testid="save-card"
                    @click="save"
                >
                    Save card
                </zen-button>
            </template>
        </template>
    </zen-slide-in>
</template>
